/* eslint-disable @nx/enforce-module-boundaries */
import { Inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Params,
  RouterStateSnapshot,
} from '@angular/router';
import * as fromClaims from '@domgen/data-access-claims';
import {
  claimActions,
  ErrorHandlerService,
  HttpInterceptorService,
  State,
} from '@domgen/data-access-claims';
import {
  AppConfig,
  Claim,
  FeatureFlagService,
  LocalStorageService,
  TimingService,
} from '@domgen/dgx-components';
import { CONFIG } from '@domgen/dgx-fe-config';
import {
  FilterFixApplianceType,
  WhiteGoodApplianceNames,
} from '@domgen/feature-repair-details';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    @Inject(CONFIG) private readonly config: AppConfig,
    private store: Store<State>,
    private storage: LocalStorageService,
    private timingService: TimingService,
    private featureFlagService: FeatureFlagService,
    private cookieService: CookieService
  ) {}

  canShowPreChatBannerBasedOnClaim(productType: string): boolean {
    const isSmartFixGenAIPOCEnabled =
      this.featureFlagService.isFeatureFlagEnabled('smart-fix-genai');
    const isProductTypeEligible =
      WhiteGoodApplianceNames.concat(FilterFixApplianceType).includes(
        productType
      ) || productType.includes('boiler');

    return isProductTypeEligible && isSmartFixGenAIPOCEnabled;
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.checkClaim(state.root.queryParams, state.url).pipe(
      switchMap(() => of(true)),
      catchError(() => of(false))
    );
  }

  checkClaim(params: Params, url: string): Observable<boolean> {
    if (params.timeSlot) {
      this.storage.setItem('timeSlot', params.timeSlot).subscribe();
    }

    const sfxRoutingCookieValue = this.cookieService.get('sfx_routing');
    const routingQueryParams = new URLSearchParams(sfxRoutingCookieValue);
    const sfxRoutingManufacturer = routingQueryParams.get('manufacturer');
    const sfxRoutingCodeDescription =
      routingQueryParams.get('code_description');

    const isSmartFixEligible =
      (sfxRoutingManufacturer &&
        sfxRoutingCodeDescription &&
        this.canShowPreChatBannerBasedOnClaim(
          sfxRoutingCodeDescription.toLowerCase()
        )) ??
      false;

    return this.storage.getItem(HttpInterceptorService.BUNDLE_TOKEN).pipe(
      switchMap((athome_aws_bundle) => {
        // if we no bundle token in params or local storage throw error
        if (
          athome_aws_bundle === null &&
          params.athome_aws_bundle === undefined
        ) {
          this.store.dispatch(
            claimActions.Error({ payload: new Error('No bundle token') })
          );
          return of(false);
        }

        const param = params.athome_aws_bundle
          ? params.athome_aws_bundle
          : athome_aws_bundle;

        const newHandoff = params.athome_aws_bundle ? true : false;

        return this.storage.setItem('athome_aws_bundle', param).pipe(
          switchMap(() => {
            // load the current claim
            return this.store.select(fromClaims.getActiveClaim).pipe(
              switchMap((data: Claim | undefined) => {
                if (data === undefined) {
                  this.timingService.startTracking('loading-claims-flow', {
                    flow: 'new',
                  });

                  this.store.dispatch(
                    claimActions.InitializeCurrentClaimV2({
                      payload: {
                        param: param,
                        newHandoff: newHandoff,
                        resolvesToSmartFix: isSmartFixEligible,
                        resolvesToMaintenance: false,
                      },
                    })
                  );

                  return throwError(true);
                } else {
                  return of(true);
                }
              }),
              take(1)
            );
          })
        );
      })
    );
  }
}
